// @flow

import * as React from 'react';

import { CornerDownRight } from 'lucide-react';
import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  selectCartCurrency,
  removeProducts,
  useDiscountDetails,
  type ProductError as CartProductError,
  type MailboxType,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import {
  selectMailboxesPrices,
  type MailboxPricesByType,
} from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Base, Fqdn } from '@gandi/ui';

import {
  addOrRemoveMailboxProducts,
  mailboxPriceParams,
} from 'src/MailboxCreate/modules/actions';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { ListRow } from '../../ListRow';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { TitleRow } from './components/Rows';
import { DiscountRow } from './components/Rows/DiscountRow';
import { MailboxPlanRows } from './components/Rows/MailboxPlanRows';

import TotalRow from './components/Rows/TotalRow';

type Props = {
  product: DecoratedProduct,
};

export default function MailboxCreate({ product }: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __, formatDate, formatPrice } = useTranslator();

  const [loading, setLoading] = React.useState(false);

  const currency = useAppSelector(selectCartCurrency);

  const prices: MailboxPricesByType = useAppSelector(state =>
    selectMailboxesPrices(state, mailboxPriceParams),
  );

  const discountDetails = useDiscountDetails({ product });

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  /**
   * Add or remove product into the cart.
   * Quantity is the quantity (string) or an event when used via
   * `inputProps` in the Counter component.
   */
  const onCounterChange = (quantity: number) => {
    // there's a mismatch between quantity and product.quantity, which leads to quantity not being updated when it should
    if (loading) {
      return;
    }
    setLoading(true);

    const fqdn = product.fqdn;
    // $FlowFixMe[incompatible-use] mailboxType does not exist in DecoratedProduct
    const mailboxType: MailboxType = product.mailboxType;
    if (fqdn) {
      // dispatch(addOrRemoveMailboxProducts(fqdn, Number(quantity), mailboxType))
      dispatch(
        addOrRemoveMailboxProducts({
          fqdn,
          mailboxType,
          quantity: Number(quantity),
          id: product.id,
        }),
      )
        .catch(() => {
          dispatch(
            notifyDanger({
              message: __('An error has occurred. Please try again.'),
            }),
          );
        })
        .then(() => {
          return dispatch(getCart());
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const fqdn = product.payload.fqdn || '';

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const getPriceByMonth = (): number | void => {
    const mailboxType = product.payload.mailbox_type;

    if (!prices || !mailboxType || !prices[mailboxType]) {
      return undefined;
    }

    const targetPriceObj = Object.values(prices[mailboxType]).find(
      p => p.minDuration === 1,
    );

    return targetPriceObj?.priceBeforeTaxes;
  };

  const priceByMonth = getPriceByMonth();
  const expirationDate = product.expirationDate;

  const detail =
    !!priceByMonth &&
    __('%(montlyPrice)s/ month/ mailbox', {
      montlyPrice: formatPrice(priceByMonth, currency),
    });

  const options = expirationDate
    ? __('Expires on %(dateEnd)s', {
        dateEnd: formatDate(expirationDate, 'LL'),
      })
    : undefined;

  return (
    <ProductItemBase
      header={
        <>
          <TitleRow
            label={
              !!productErrors?.length && fqdn ? (
                <Fqdn>{fqdn}</Fqdn>
              ) : (
                __('Mailbox')
              )
            }
            productType={product.productType}
            handleRemoveAction={removeFromCart}
          />
          <ProductErrorItem errors={productErrors} productId={product.id} />

          <Base flex gap="spacing-2x" flexCol>
            <MailboxPlanRows
              product={product}
              onChange={onCounterChange}
              isLoading={loading}
            />
            {fqdn && (
              <ListRow
                intent="muted"
                icon={<CornerDownRight size={16} />}
                title={<Fqdn color="base">{fqdn}</Fqdn>}
                options={options ? [options] : undefined}
                optionsArrow={false}
              />
            )}
          </Base>
        </>
      }
      footer={
        <TotalRow
          hasTaxes={false}
          totalPrice={
            product.computedTotalPrices?.beforeTaxes ??
            product.total?.before_taxes
          }
          priceCrossedOut={product.total?.before_discount}
          hasDiscount={!!discountDetails?.length}
        />
      }
    >
      {!!detail && (
        <ListRow
          intent="muted"
          icon={<CornerDownRight size={16} />}
          title={detail}
          optionsArrow={false}
        />
      )}
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          price={discount.price}
        />
      ))}
    </ProductItemBase>
  );
}
